import { getCardPublicKey } from "../api/api";
import {
  handleUpdatePin as handleUpdatePinHelper,
  handleShowCardDetails as handleShowCardDetailsHelper,
} from "./secureCardMethods";

// Re-declare the Rain type if it's not globally available in this context
// or if you want to ensure type safety without relying on global declarations.
// This is important if these functions are used outside of the React component
// where the global type is declared.
declare global {
  interface Window {
    rain?: {
      generateSessionId: (
        pemKey: string
      ) => Promise<{ secretKey: string; sessionId: string }>;
      decryptSecret: (
        data: string,
        iv: string,
        secretKey: string
      ) => Promise<string>;
    };
  }
}

// Ensure the Rain.js script is loaded.
// This utility function can be called once before using the exported functions.
export const ensureRainScriptLoaded = async (): Promise<boolean> => {
  if (typeof window === "undefined") {
    console.warn(
      "Rain.js script loading skipped: Not in a browser environment."
    );
    return false;
  }

  if (window.rain) {
    return true;
  }

  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "/rain.js"; // Adjust the path if rain.js is located elsewhere
    script.async = true;
    script.onload = () => {
      console.log("rain.js script loaded successfully.");
      resolve(true);
    };
    script.onerror = (e) => {
      console.error("Error loading rain.js script:", e);
      resolve(false);
    };
    document.body.appendChild(script);

    // Polling fallback in case onload doesn't fire as expected or script is already cached
    const checkInterval = setInterval(() => {
      if (window.rain) {
        clearInterval(checkInterval);
        resolve(true);
      }
    }, 500);

    // Set a timeout to stop polling if it takes too long
    setTimeout(() => {
      if (!window.rain) {
        clearInterval(checkInterval);
        console.warn("rain.js script did not load within the expected time.");
        resolve(false);
      }
    }, 10000); // 10 seconds timeout
  });
};

export interface DecryptedCardDetails {
  cardNumber: string;
  cvc: string;
  expiryMonth: string;
  expiryYear: string;
}

interface SignTypedDataFunction {
  (params: any): Promise<string>;
}

/**
 * Retrieves and decrypts credit card details (PAN, CVC, Expiry).
 * This function handles the secure key exchange and decryption process.
 *
 * @param signTypedData - A function (e.g., from wagmi) to sign EIP-712 typed data.
 * @param address - The wallet address of the user.
 * @returns A promise that resolves to `DecryptedCardDetails` on success, or throws an error.
 */
export async function showCardDetails(
  signTypedData: SignTypedDataFunction,
  address: string
): Promise<DecryptedCardDetails> {
  if (!window.rain) {
    throw new Error(
      "Rain SDK is not loaded. Please ensure 'ensureRainScriptLoaded()' has been called and resolved successfully."
    );
  }

  if (!address) {
    throw new Error("Wallet address is required to show card details.");
  }

  if (!signTypedData || typeof signTypedData !== "function") {
    throw new Error("A valid signTypedData function is required.");
  }

  try {
    // Step 1: Fetch PEM public key for session
    const pemKeyResponse = await getCardPublicKey();
    const pemKey = pemKeyResponse.data;

    // Step 2: Generate session ID and secret key from Rain SDK
    const { secretKey, sessionId } = await window.rain.generateSessionId(
      pemKey
    );

    // Step 3: Securely fetch encrypted card secrets via EIP-712-signed SDK call
    const { success, secrets, message } = await handleShowCardDetailsHelper(
      sessionId
      // Assuming handleShowCardDetailsHelper will use the global signTypedData context
      // or you need to pass it explicitly if the helper is not a hook and needs it
    );

    if (!success || !secrets) {
      throw new Error(
        message || "Unable to fetch encrypted card details from SDK."
      );
    }

    // Step 4: Decrypt PAN and CVC using Rain SDK
    const decryptedPan = await window.rain.decryptSecret(
      secrets.encryptedPan.data,
      secrets.encryptedPan.iv,
      secretKey
    );
    const decryptedCvc = await window.rain.decryptSecret(
      secrets.encryptedCvc.data,
      secrets.encryptedCvc.iv,
      secretKey
    );

    const month = String(secrets.ExpirationMonth).padStart(2, "0");
    const year = String(secrets.ExpirationYear).padStart(4, "0");

    return {
      cardNumber: decryptedPan,
      cvc: decryptedCvc,
      expiryMonth: month,
      expiryYear: year,
    };
  } catch (error) {
    console.error("Error in showCardDetails:", error);
    throw new Error(
      `Failed to show card details: ${
        error instanceof Error ? error.message : "An unknown error occurred."
      }`
    );
  }
}

/**
 * Updates the PIN for the user's card.
 * This function handles the secure key exchange and PIN update process.
 *
 * @param updatedPin - The new PIN (string of digits).
 * @param signTypedData - A function (e.g., from wagmi) to sign EIP-712 typed data.
 * @param address - The wallet address of the user.
 * @returns A promise that resolves to `true` on success, or throws an error.
 */
export async function updateCardPin(
  updatedPin: string,
  signTypedData: SignTypedDataFunction,
  address: string
): Promise<boolean> {
  if (!window.rain) {
    throw new Error(
      "Rain SDK is not loaded. Please ensure 'ensureRainScriptLoaded()' has been called and resolved successfully."
    );
  }

  if (!address) {
    throw new Error("Wallet address is required to update PIN.");
  }

  if (!signTypedData || typeof signTypedData !== "function") {
    throw new Error("A valid signTypedData function is required.");
  }

  if (
    updatedPin.length < 4 ||
    updatedPin.length > 12 ||
    !/^\d+$/.test(updatedPin)
  ) {
    throw new Error(
      "Invalid PIN: Must be 4-12 digits long and contain only numbers."
    );
  }

  try {
    // Step 1: Fetch PEM public key for session
    const pemKeyResponse = await getCardPublicKey();
    const pemKey = pemKeyResponse.data;

    // Step 2: Generate session ID and secret key from Rain SDK
    const { secretKey, sessionId } = await window.rain.generateSessionId(
      pemKey
    );

    // Step 3: Call SDK handler for secure PIN update
    const { success, message } = await handleUpdatePinHelper({
      pin: updatedPin,
      secretKey,
      sessionId,
      // Assuming handleUpdatePinHelper will use the global signTypedData context
      // or you need to pass it explicitly if the helper is not a hook and needs it
    });

    if (!success) {
      throw new Error(message || "Unable to update PIN via SDK.");
    }

    return true;
  } catch (error) {
    console.error("Error in updateCardPin:", error);
    throw new Error(
      `Failed to update card PIN: ${
        error instanceof Error ? error.message : "An unknown error occurred."
      }`
    );
  }
}
